import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../utils/cn';

const focusRing =
  'focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(128,110,225,.3)]';

export type SortDir = 'asc' | 'desc' | null;

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  children: React.ReactNode;
  className?: string;
  /** Wraps the table in an overflow-x-auto container so wide tables scroll instead of breaking layout. */
  scrollable?: boolean;
}

/**
 * Semantic `<table>` primitive with DFX5 styling. This is a new primitive introduced in the
 * Cycle 1 design-system pass — existing raw `<table>` markup (LOEListPage, VersionHistory, etc.)
 * is not migrated to it this cycle. RateManagementPage and CustomerListPage were migrated in
 * later cycles.
 */
export const Table: React.FC<TableProps> = ({ children, className, scrollable = true, ...rest }) => {
  const table = (
    <table className={cn('w-full border-collapse text-sm', className)} {...rest}>
      {children}
    </table>
  );

  if (!scrollable) return table;

  return <div className="w-full overflow-x-auto">{table}</div>;
};

// ---------------------------------------------------------------------------
// THead / TBody
// ---------------------------------------------------------------------------

export interface THeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
  className?: string;
  /** className applied to the wrapping header row rather than the <thead> element. */
  rowClassName?: string;
}

/** Header section. Wraps its `Th` children in a single header row styled with the muted background. */
export const THead: React.FC<THeadProps> = ({ children, className, rowClassName, ...rest }) => (
  <thead className={className} {...rest}>
    <tr className={cn('bg-[var(--muted)]', rowClassName)}>{children}</tr>
  </thead>
);

export type TBodyProps = React.HTMLAttributes<HTMLTableSectionElement>;

export const TBody: React.FC<TBodyProps> = ({ children, className, ...rest }) => (
  <tbody className={className} {...rest}>
    {children}
  </tbody>
);

// ---------------------------------------------------------------------------
// Tr
// ---------------------------------------------------------------------------

export type TrProps = React.HTMLAttributes<HTMLTableRowElement>;

/** Body row: hairline bottom border + subtle hover highlight. */
export const Tr: React.FC<TrProps> = ({ children, className, ...rest }) => (
  <tr
    className={cn(
      'border-b border-[var(--border)] transition-colors duration-150 hover:bg-[var(--muted)]/50',
      className
    )}
    {...rest}
  >
    {children}
  </tr>
);

// ---------------------------------------------------------------------------
// Th
// ---------------------------------------------------------------------------

export interface ThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children?: React.ReactNode;
  className?: string;
  /** Right-aligns the header label to match a numeric column. */
  numeric?: boolean;
  /** Renders a clickable sort affordance (Lucide chevron) reflecting `sortDir`. */
  sortable?: boolean;
  sortDir?: SortDir;
  onSort?: () => void;
}

const thBase =
  'px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)] whitespace-nowrap';

export const Th: React.FC<ThProps> = ({
  children,
  className,
  numeric = false,
  sortable = false,
  sortDir = null,
  onSort,
  ...rest
}) => {
  const ariaSort = !sortable ? undefined : sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none';

  const chevron = sortable
    ? sortDir === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
      : sortDir === 'desc'
        ? <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
        : <ChevronsUpDown className="w-3.5 h-3.5 opacity-50" aria-hidden="true" />
    : null;

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={cn(thBase, numeric ? 'text-right' : 'text-left', className)}
      {...rest}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            'inline-flex items-center gap-1 bg-transparent p-0 cursor-pointer rounded-sm',
            'text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors duration-150',
            focusRing,
            numeric && 'w-full justify-end'
          )}
        >
          <span>{children}</span>
          {chevron}
        </button>
      ) : (
        children
      )}
    </th>
  );
};

// ---------------------------------------------------------------------------
// Td
// ---------------------------------------------------------------------------

export interface TdProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children?: React.ReactNode;
  className?: string;
  /** Right-aligns and applies tabular-nums, for numeric column values. */
  numeric?: boolean;
}

export const Td: React.FC<TdProps> = ({ children, className, numeric = false, ...rest }) => (
  <td
    className={cn('px-4 py-3 text-sm text-[var(--foreground)]', numeric && 'text-right tabular-nums', className)}
    {...rest}
  >
    {children}
  </td>
);

// ---------------------------------------------------------------------------
// useSort
// ---------------------------------------------------------------------------

export interface UseSortResult<T> {
  rows: T[];
  sortKey: keyof T | null;
  dir: SortDir;
  toggle: (key: keyof T) => void;
}

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b));
}

/**
 * Client-side sort helper for the Table primitive.
 *
 * `toggle(key)` cycles: none -> asc -> desc -> none (back to the original, stable order).
 * Switching to a different key always starts at ascending. The underlying sort is stable —
 * rows that compare equal keep their original relative order.
 */
export function useSort<T>(rows: T[], initialKey?: keyof T): UseSortResult<T> {
  const [sortKey, setSortKey] = useState<keyof T | null>(initialKey ?? null);
  const [dir, setDir] = useState<SortDir>(initialKey ? 'asc' : null);

  const toggle = (key: keyof T) => {
    if (sortKey !== key) {
      setSortKey(key);
      setDir('asc');
      return;
    }

    if (dir === 'asc') {
      setDir('desc');
    } else if (dir === 'desc') {
      setDir(null);
      setSortKey(null);
    } else {
      setDir('asc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortKey || !dir) return rows;

    const indexed = rows.map((row, index) => ({ row, index }));
    indexed.sort((a, b) => {
      const cmp = compareValues(a.row[sortKey], b.row[sortKey]);
      const ordered = dir === 'asc' ? cmp : -cmp;
      return ordered !== 0 ? ordered : a.index - b.index;
    });

    return indexed.map((entry) => entry.row);
  }, [rows, sortKey, dir]);

  return { rows: sortedRows, sortKey, dir, toggle };
}
